import type { JSX, ParentComponent, VoidComponent } from "solid-js";
import { createSignal, createUniqueId, Show, untrack } from "solid-js";
import type { IconName } from "../../icon/Icon";
import { createDraggable } from "@thisbeyond/solid-dnd";
import Handlebar from "../Handlebar";
import { useTranslation } from "../../../state/i18n";
import type { Category, FieldType, WithClassList } from "../../../types";
import { IconButtonMenu } from "../../listItemMenu/ListItemMenu";
import InfoButton from "../../infoButton/InfoButton";
import { getIconCategoryColor } from "../../../utils/iconColorUtil";
import InlineEditor from "../../inlineEditor/InlineEditor";
import { MAX_CHARACTERS_SM } from "../../../constants";
import { KEY } from "../../../utils/keyboardUtil";
import Text from "../../text/Text";
import type { Favourite } from "../../../services/FavouriteService";
import { DynamicValueIconTile } from "../../icon/IconTile";

type GenericDraggableProps = {
  label: string;
  description: string;
  icon: IconName;
  type: FieldType;
  category: Category;
  onDblClick: () => void;
};

export type DraggableFieldProps = GenericDraggableProps;

export type DraggableFavouriteProps = {
  favourite: Favourite;
  onEdit: (value: string) => void;
  onDelete: () => void;
} & GenericDraggableProps;

export type DraggableData = {
  overlay: () => JSX.Element;
  type: FieldType;
  favourite?: Favourite;
};

type Variant = "disabled" | "active" | "default";

const Outline: ParentComponent<{ containerVariant: Variant; icon: IconName; category: Category }> = (props) => (
  <div
    class="flex h-9.5 items-center rounded-sm border px-2 py-0.75 text-base-medium text-primary"
    classList={{
      "border-subtle bg-surface-default": props.containerVariant === "default",
      "border-subtle bg-surface-disabled": props.containerVariant === "disabled",
      "relative cursor-grabbing border-default bg-surface-default": props.containerVariant === "active",
    }}
  >
    <Handlebar
      classList={{
        "icon-disabled": props.containerVariant === "disabled",
        "cursor-grab": props.containerVariant === "default",
      }}
    />
    <div class="ml-1.5">
      <DynamicValueIconTile
        icon={props.icon}
        iconClassList={{
          "icon-disabled": props.containerVariant === "disabled",
          [`${getIconCategoryColor(props.category)}`]: true,
        }}
        size="md"
      />
    </div>
    {props.children}
  </div>
);

const Label: VoidComponent<WithClassList<{ label: string; disabled: boolean }>> = (props) => (
  <Text
    // oxlint-disable-next-line better-tailwindcss/no-restricted-classes -- one extra pixel to account for border when opening/closing inline editor
    class="grow pl-[9px]"
    classList={{ ...props.classList, "text-disabled": props.disabled }}
    text={props.label}
    truncate
  />
);

const Button: VoidComponent<{
  onDblClick: () => void;
  containerVariant: Variant;
  icon: IconName;
  category: Category;
  label: string;
  draggableType: "field" | "favourite";
}> = (props) => (
  <button
    onDblClick={() => props.onDblClick()}
    onKeyUp={(event) => {
      if (event.code === KEY.Space || event.code === KEY.Enter) {
        props.onDblClick();
      }
    }}
    class="block w-full overflow-hidden rounded-sm text-left focus-visible:outline-default"
  >
    <Outline containerVariant={props.containerVariant} icon={props.icon} category={props.category}>
      <Label
        label={props.label}
        disabled={props.containerVariant === "disabled"}
        classList={{ "pr-8.5": props.draggableType === "favourite", "pr-4": props.draggableType === "field" }}
      />
    </Outline>
  </button>
);

export const DraggableField: VoidComponent<DraggableFieldProps> = (props) => {
  const { t } = useTranslation();

  const container = (containerVariant: Variant) => (
    <div class="relative" classList={{ "shadow-elevation-light-3": containerVariant === "active" }}>
      <Button
        onDblClick={props.onDblClick}
        containerVariant={containerVariant}
        icon={props.icon}
        category={props.category}
        label={props.label}
        draggableType="field"
      />
      <InfoButton
        title={props.label}
        description={props.description}
        classList={{
          "absolute inset-y-0 right-2 my-auto": true,
        }}
        ariaLabel={t(($) => $.FIELD_DESCRIPTION_ARIA_LABEL, { field_label: props.label })}
      />
    </div>
  );

  const draggable = createDraggable(createUniqueId(), {
    overlay: () => container("active"),
    type: untrack(() => props.type),
  });

  return <div use:draggable>{container(draggable.isActiveDraggable ? "disabled" : "default")}</div>;
};

export const DraggableFavourite: VoidComponent<DraggableFavouriteProps> = (props) => {
  const { t } = useTranslation();
  const [isEditing, setEditing] = createSignal(false);

  const draggable = createDraggable(createUniqueId(), {
    overlay: () => (
      <div class="relative h-9.5">
        <Button
          onDblClick={props.onDblClick}
          containerVariant="active"
          icon={props.icon}
          category={props.category}
          label={props.label}
          draggableType="favourite"
        />
        <div class="absolute inset-y-0 right-1.5 my-auto flex items-center">
          <IconButtonMenu disabled={false} items={[]} aria-label={t(($) => $.ACTIONS_ARIA)} />
        </div>
      </div>
    ),
    type: untrack(() => props.type),
    favourite: untrack(() => props.favourite),
  });

  return (
    <Show
      when={!isEditing()}
      fallback={
        <Outline containerVariant="default" icon={props.icon} category={props.category}>
          <div class="overflow-hidden px-1">
            <InlineEditor
              label={t(($) => $.FAVOURITE_EDIT_NAME_LABEL)}
              value={props.label}
              setValue={(value) => {
                props.onEdit(value);
              }}
              open={isEditing()}
              setOpen={(open) => setEditing(open)}
              maxLength={MAX_CHARACTERS_SM}
            />
          </div>
        </Outline>
      }
    >
      <div class="relative">
        <div use:draggable>
          <Button
            onDblClick={props.onDblClick}
            containerVariant={draggable.isActiveDraggable ? "disabled" : "default"}
            icon={props.icon}
            category={props.category}
            label={props.label}
            draggableType="favourite"
          />
        </div>
        <div class="absolute inset-y-0 right-1.5 my-auto flex items-center">
          <IconButtonMenu
            disabled={(draggable.isActiveDraggable ? "disabled" : "default") === "disabled"}
            items={[
              [
                {
                  value: "rename",
                  type: "action",
                  onClick: () => setEditing(true),
                  variant: "text",
                  label: t(($) => $.RENAME_BUTTON),
                },
                {
                  value: "delete",
                  type: "action",
                  onClick: props.onDelete,
                  variant: "text",
                  label: t(($) => $.DELETE_BUTTON),
                },
              ],
            ]}
            aria-label={t(($) => $.ACTIONS_ARIA)}
          />
        </div>
      </div>
    </Show>
  );
};
